import { httpClient } from '../funcs'
import { saveLegacyToken, saveOAuth2Token, getToken } from './saveToken'
import { isOAuth2AccessJwt } from './oauth2Tokens'

/** Один обмен на code — защита от seamless/prefetch и двойной инициализации SPA. */
const partnerExchangeInFlight = new Map<string, Promise<boolean>>()

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function cleanPartnerAuthUrl(pathname: string): string {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    const cleanPath = pathname.endsWith('/auth/partner') ? '/profile' : pathname
    return cleanPath + url.search + url.hash
}

function persistPartnerTokens(accessToken: string, refreshToken: string): void {
    if (refreshToken || isOAuth2AccessJwt(accessToken)) {
        saveOAuth2Token({
            access_token: accessToken,
            refresh_token: refreshToken,
        }, { persistRefresh: true })
    } else {
        saveLegacyToken(accessToken)
    }
}

/**
 * Одноразовый вход в ЛК по ссылке от партнёра.
 *
 * Партнёр открывает пользователя на:
 *   https://desktop.doki.online/auth/partner?code=<one-time-code>
 *
 * Code обменивается на OAuth2-токены через POST /external/exchange-login-code
 * (токены не попадают в URL).
 */
async function exchangePartnerCodeOnce(code: string): Promise<boolean> {
    const exchangeKey = `partner_code_exchanged_${code}`

    if (sessionStorage.getItem(exchangeKey) === 'done' && getToken()) {
        window.history.replaceState({}, '', cleanPartnerAuthUrl(window.location.pathname))
        return true
    }

    if (getToken()) {
        sessionStorage.setItem(exchangeKey, 'done')
        window.history.replaceState({}, '', cleanPartnerAuthUrl(window.location.pathname))
        return true
    }

    if (!process.env.REACT_APP_API_URL) {
        console.error('REACT_APP_API_URL is not configured')
        return false
    }

    sessionStorage.setItem(exchangeKey, 'pending')

    const maxAttempts = 3
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const { data } = await httpClient.post('/external/exchange-login-code', { code })

            const accessToken = data?.access_token
            const refreshToken = data?.refresh_token || ''

            if (!accessToken) {
                console.error('Partner login exchange: empty access_token in response')
                sessionStorage.removeItem(exchangeKey)
                return false
            }

            persistPartnerTokens(accessToken, refreshToken)
            sessionStorage.setItem(exchangeKey, 'done')
            window.history.replaceState({}, '', cleanPartnerAuthUrl(window.location.pathname))
            return true
        } catch (error: any) {
            const status = error?.response?.status
            console.error(
                'Partner login exchange failed:',
                status,
                error?.response?.data || error,
                `(attempt ${attempt + 1}/${maxAttempts})`,
            )

            if (getToken()) {
                sessionStorage.setItem(exchangeKey, 'done')
                window.history.replaceState({}, '', cleanPartnerAuthUrl(window.location.pathname))
                return true
            }

            // 410 — code уже consumed параллельным запросом; idempotent retry на бэкенде.
            if (status === 410 && attempt < maxAttempts - 1) {
                await sleep(150 * (attempt + 1))
                continue
            }

            sessionStorage.removeItem(exchangeKey)
            window.history.replaceState({}, '', '/main?auth=1')
            return false
        }
    }

    return false
}

export async function bootstrapPartnerAuthFromQuery(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (!code) {
        return false
    }

    const inFlight = partnerExchangeInFlight.get(code)
    if (inFlight) {
        return inFlight
    }

    const promise = exchangePartnerCodeOnce(code)
    partnerExchangeInFlight.set(code, promise)

    try {
        return await promise
    } finally {
        partnerExchangeInFlight.delete(code)
    }
}
